import { ModalInput } from '@components/utils/inputElements/textInput';
import NewTabLink from '@components/utils/links/newTabLink';
import MdText from '@components/utils/mdText';
import { Caption } from '@components/utils/text';
import {
    CONSENTS,
    FORM_OPT_IN_CHANNELS_KEY,
    FORM_OPT_IN_KEY,
} from '@constants';
import { StyledProps, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
    OutboundLinkContext,
    OutboundLinkDestinationCategory,
    OutboundLinkShape,
} from '@enums';
import { ComponentConsentsEmail } from '@graphql/generated/graphql';
import { FormErrorMessage, HStack } from '@lib/uikit';
import { TextVariant } from '@lib/uikit/styles/text';
import { useFormikContext } from 'formik';
import { Trans, useTranslation } from 'next-i18next';
import { useEffect, useMemo } from 'react';

type Props = {
    element: ComponentConsentsEmail;
    companyName: string;
    outboundLinkContext: OutboundLinkContext;
    textVariant?: TextVariant;
};

const CustomText = styled.div<StyledProps<{ textColor: string }>>`
    font: ${({ theme }) => theme.fonts.caption};
    * {
        color: ${({ theme, textColor }) =>
            textColor ?? theme.palette.text.secondary};
        font: ${({ theme }) => theme.fonts.caption};
    }
`;

const CaptionLink = styled(NewTabLink)`
    font: ${(props) => props.theme.fonts.caption};
    text-decoration: underline;
`;

const CaptionError = styled(FormErrorMessage)`
    margin-top: 0;
`;

const BoxInput = styled(ModalInput)`
    height: ${(props) => props.theme.spacing.md};
    width: ${(props) => props.theme.spacing.sm};
`;

const Stack = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.spacing.xxs};
`;

export const EmailConsent = ({
    element,
    outboundLinkContext,
    companyName,
    textVariant = TextVariant.Light,
}: Props) => {
    const theme = useTheme();
    const { t } = useTranslation(CONSENTS);
    const { errors, getFieldProps, setFieldValue } =
        useFormikContext<Record<string, string>>();

    const isExplicit = useMemo(
        () => !element?.ImplicitConsent,
        [element.ImplicitConsent]
    );
    const textColor = useMemo(
        () =>
            textVariant === TextVariant.Dark
                ? theme.palette.common.white
                : theme.palette.text.primary,
        [textVariant, theme]
    );

    useEffect(() => {
        setFieldValue(FORM_OPT_IN_CHANNELS_KEY, element.Channels);
    }, [element.Channels, setFieldValue]);

    return (
        <Stack>
            <Stack>
                <HStack spacing='xs'>
                    {isExplicit && (
                        <BoxInput
                            type='checkbox'
                            required={element.IsRequired}
                            defaultChecked={
                                getFieldProps(`['${FORM_OPT_IN_KEY}']`).value
                            }
                            {...getFieldProps(`['${FORM_OPT_IN_KEY}']`)}
                        />
                    )}
                    {element.CustomText ? (
                        <CustomText textColor={textColor}>
                            <MdText
                                markdown={element.CustomText}
                                textColor={textColor}
                            />
                        </CustomText>
                    ) : (
                        <Caption color={textColor}>
                            {t(isExplicit ? 'explicit.text' : 'implicit.text')}
                        </Caption>
                    )}
                </HStack>
                {element.PrivacyPolicyURL && (
                    <Caption color={textColor}>
                        <Trans
                            t={t}
                            i18nKey={
                                isExplicit
                                    ? 'explicit.privacy'
                                    : 'implicit.privacy'
                            }
                            values={{ company: companyName }}
                            components={{
                                privacyPolicy: (
                                    <CaptionLink
                                        destination_category={
                                            OutboundLinkDestinationCategory.CompanyDocument
                                        }
                                        context={outboundLinkContext}
                                        shape={OutboundLinkShape.InlineText}
                                        destination_name='Privacy Policy'
                                        href={element.PrivacyPolicyURL}
                                    >
                                        {null}
                                    </CaptionLink>
                                ),
                            }}
                        />
                    </Caption>
                )}
            </Stack>
            {errors[FORM_OPT_IN_KEY] && (
                <CaptionError message={errors[FORM_OPT_IN_KEY]} />
            )}
        </Stack>
    );
};
